import type {
  DefineUnitInput,
  RemovedUnit,
  UnitPriceInput,
  UnitPrices,
} from '@/api/analytics'
import type { ConnectedCollection } from '@/api/contract'
import type { EntitiesResponse, HouseholdEntity } from '@/api/entities'
import type {
  DisposalInput,
  EffectiveValuation,
  HoldingEvent,
  HoldingHistory,
  HoldingRow,
  HoldingsFlags,
  HoldingsResponse,
  PrivateHoldingRow,
  UnpricedHolding,
  ValuationDraft,
  ValuationRow,
} from '@/api/manual'
import type {
  OwnershipConsequence,
  OwnershipEdgeRow,
  OwnershipPreview,
  OwnershipRefusal,
  OwnershipRefusalReason,
} from '@/api/ownership'
import type {
  SavedGrant,
  WarrantGrantDraft,
  WarrantGrantEdit,
  WarrantGrantView,
  WarrantsResponse,
} from '@/api/warrants'
import type { OwnershipEdge } from '@/domain/entity'
import type { UnitDefinition } from '@/domain/units'
import type { SourceState } from '@/domain/valuation'
import type { ManualAccount, ManualDocument, ManualPosition } from '@/collectors/manual'
import type { Disposal } from '@/domain/private-holding'
import type { Quantity } from '@/domain/money'
import { figureFormat, householdLocale, localeFor } from '~/format/locale'

export type {
  SavedGrant,
  WarrantGrantDraft,
  WarrantGrantEdit,
  WarrantGrantView,
  WarrantsResponse,
}

export type {
  Disposal,
  DisposalInput,
  EffectiveValuation,
  EntitiesResponse,
  HoldingRow,
  HoldingsFlags,
  HoldingsResponse,
  HouseholdEntity,
  OwnershipConsequence,
  OwnershipEdge,
  OwnershipEdgeRow,
  OwnershipPreview,
  OwnershipRefusal,
  OwnershipRefusalReason,
  SourceState,
  UnpricedHolding,
  ManualAccount,
  ManualDocument,
  ManualPosition,
  PrivateHoldingRow,
  ValuationDraft,
  ValuationRow,
  HoldingEvent,
  HoldingHistory,
}

export interface OwnershipResponse {
  edges: OwnershipEdgeRow[]
}

export interface DeletedResponse {
  deleted: boolean
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const BASE_URL: string = import.meta.env.VITE_API_BASE ?? '/api'

/** The refusal that means "touch your passkey again", not "you are signed out". */
export const REASSERTION_REQUIRED = 'reassertion_required'

/** What the server answers wherever nothing about a request names a signed-in person. */
const UNAUTHENTICATED = 'unauthenticated'

/**
 * The route that exists to ask whether anyone is signed in. Its 401 is the
 * answer being asked for rather than news about it, and announcing it would
 * have the thing that listens react to what it just requested.
 */
const SESSION_PATH = '/auth/session'

/** The ceremony that answers the guard, and the one request that must not retry. */
const REASSERT_PATH = '/auth/reassert'

type SignedOutListener = () => void

const signedOut = new Set<SignedOutListener>()

/**
 * Told when a request is refused because there is no session behind it any
 * more.
 *
 * One rule here rather than a check at every call site: a session expires while
 * somebody is reading a page, and a rule each new query has to remember is one
 * the next query will not. Without it the app goes on rendering — navigation
 * works, nothing loads — until a reload happens to ask again.
 */
export function onSignedOut(listener: SignedOutListener): () => void {
  signedOut.add(listener)
  return () => {
    signedOut.delete(listener)
  }
}

type ReassertionCeremony = () => Promise<void>

let reassertion: ReassertionCeremony | undefined

/**
 * How this client answers a request to touch the passkey again.
 *
 * Installed once, in the module that owns the ceremonies, rather than wrapped
 * around each mutation that might need it. The server refuses a whole list of
 * destructive acts on a session that has merely been left open, and a retry
 * every new mutation has to remember is one the next mutation will not — which
 * is how a guard turns into a dead end, where the only thing on screen is a
 * refusal telling somebody to press a finger nothing is waiting for.
 */
export function onReassertionRequired(ceremony: ReassertionCeremony): () => void {
  reassertion = ceremony
  return () => {
    if (reassertion === ceremony) reassertion = undefined
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    return await send<T>(path, init)
  } catch (error) {
    if (reassertion === undefined || !asksForTheKeyAgain(path, error)) throw error

    await touchTheKey(reassertion)
    // The guard refuses before the handler runs, so nothing was written and the
    // same request can be sent again. Every body here is a string, which is
    // what makes sending it twice possible at all.
    return send<T>(path, init)
  }
}

let touching: Promise<void> | undefined

/**
 * One ceremony at a time, however many requests were refused.
 *
 * The server keeps a single outstanding challenge per ceremony and holder, and
 * replaces it when a second is asked for — so two ceremonies racing would spend
 * each other's challenge and one of the two acts would fail on a passkey the
 * holder did in fact touch. They wait on the same touch instead.
 */
async function touchTheKey(ceremony: ReassertionCeremony): Promise<void> {
  touching ??= ceremony().finally(() => {
    touching = undefined
  })
  return touching
}

/**
 * Asked at most once per request, and never of the ceremony's own calls: a
 * reassertion that was itself refused would go round for ever.
 */
function asksForTheKeyAgain(path: string, error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.status !== 401 || error.message !== REASSERTION_REQUIRED) return false
  return !path.startsWith(REASSERT_PATH)
}

async function send<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init?.headers,
    },
  })

  const body: unknown = await response.json().catch(() => undefined)
  if (!response.ok) {
    const error = new ApiError(response.status, errorMessageOf(body) ?? `${path} failed`, body)
    if (saysThereIsNoSession(path, error)) for (const listener of [...signedOut]) listener()
    throw error
  }
  return body as T
}

/**
 * Read off the body rather than off the status, because several 401s are not
 * this one. `reassertion_required` is a live session being asked to prove
 * itself before a destructive act, and treating it as a sign-out would throw
 * somebody to the login screen at the moment they were told to touch their key;
 * `authentication_failed` is a ceremony that did not convince, which says
 * nothing about the session that was open while it ran.
 */
function saysThereIsNoSession(path: string, error: ApiError): boolean {
  if (error.status !== 401 || path.startsWith(SESSION_PATH)) return false
  return error.message === UNAUTHENTICATED
}

function errorMessageOf(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const { error } = body as { error: unknown }
    if (typeof error === 'string') return error
  }
  return undefined
}

export function withQuery(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value)
  }
  const query = search.toString()
  return query ? `${path}?${query}` : path
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

/** What a hand-entered write answers with beyond its own row: the collection it started. */
export interface Collected {
  collection?: ConnectedCollection
}

export const api = {
  holdings: (date?: string): Promise<HoldingsResponse> =>
    apiFetch(withQuery('/holdings', { date })),

  entities: (): Promise<EntitiesResponse> => apiFetch('/entities'),

  /** Retired ones too, which only the page that manages them has any use for. */
  everyEntity: (): Promise<EntitiesResponse> => apiFetch('/entities?include=retired'),

  createEntity: (input: { name: string; kind: HouseholdEntity['kind'] }): Promise<HouseholdEntity> =>
    apiFetch('/entities', json(input)),

  renameEntity: (key: string, name: string): Promise<HouseholdEntity> =>
    apiFetch(`/entities/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  retireEntity: (key: string): Promise<HouseholdEntity> =>
    apiFetch(`/entities/${encodeURIComponent(key)}/retirement`, { method: 'PUT' }),

  unretireEntity: (key: string): Promise<HouseholdEntity> =>
    apiFetch(`/entities/${encodeURIComponent(key)}/retirement`, { method: 'DELETE' }),

  ownership: (): Promise<OwnershipResponse> => apiFetch('/ownership'),

  /** Read on every keystroke of a draft, so it never writes anything itself. */
  previewOwnership: (edges: readonly OwnershipEdge[]): Promise<OwnershipPreview> =>
    apiFetch('/ownership/preview', json(edges)),

  saveOwnershipEdge: (edge: OwnershipEdge): Promise<OwnershipEdgeRow> =>
    apiFetch('/ownership', json(edge)),

  deleteOwnershipEdge: (id: number): Promise<DeletedResponse> =>
    apiFetch(`/ownership/${id}`, { method: 'DELETE' }),

  manualSource: (): Promise<ManualDocument> => apiFetch('/manual'),

  saveManualAccount: (account: ManualAccount): Promise<ManualAccount & Collected> =>
    apiFetch(`/manual/accounts/${encodeURIComponent(account.stableKey)}`, {
      method: 'PUT',
      body: JSON.stringify(account),
    }),

  deleteManualAccount: (stableKey: string): Promise<DeletedResponse & Collected> =>
    apiFetch(`/manual/accounts/${encodeURIComponent(stableKey)}`, { method: 'DELETE' }),

  valuations: (): Promise<{ events: ValuationRow[] }> => apiFetch('/manual/valuations'),

  effectiveValuations: (date?: string): Promise<{ date: string; valuations: EffectiveValuation[] }> =>
    apiFetch(withQuery('/manual/valuations/effective', { date })),

  saveValuation: (draft: ValuationDraft): Promise<ValuationRow & Collected> =>
    apiFetch('/manual/valuations', json(draft)),

  deleteValuation: (id: number): Promise<DeletedResponse & Collected> =>
    apiFetch(`/manual/valuations/${id}`, { method: 'DELETE' }),

  privateHoldings: (date?: string): Promise<{ date: string; holdings: PrivateHoldingRow[] }> =>
    apiFetch(withQuery('/manual/holdings', { date })),

  recordDisposal: (instrumentId: number, draft: DisposalInput): Promise<PrivateHoldingRow & Collected> =>
    apiFetch(`/manual/holdings/${instrumentId}/disposals`, json(draft)),

  deleteDisposal: (instrumentId: number, date: string): Promise<PrivateHoldingRow & Collected> =>
    apiFetch(`/manual/holdings/${instrumentId}/disposals/${date}`, { method: 'DELETE' }),

  holdingHistory: (instrumentId: number): Promise<HoldingHistory> =>
    apiFetch(`/manual/holdings/${instrumentId}/history`),

  warrants: (date?: string): Promise<WarrantsResponse> => apiFetch(withQuery('/warrants', { date })),

  /**
   * The body is the route's own input type, so a term the schema stops
   * accepting stops typechecking here rather than failing as a 400 in front of
   * whoever was entering the grant.
   */
  saveWarrantGrant: (draft: WarrantGrantDraft): Promise<SavedGrant> =>
    apiFetch('/warrants', json(draft)),

  editWarrantGrant: (id: number, edit: WarrantGrantEdit): Promise<SavedGrant> =>
    apiFetch(`/warrants/${id}`, { method: 'PATCH', body: JSON.stringify(edit) }),

  deleteWarrantGrant: (id: number): Promise<DeletedResponse> =>
    apiFetch(`/warrants/${id}`, { method: 'DELETE' }),

  unitPrices: (key: string): Promise<UnitPrices> =>
    apiFetch(`/analytics/units/${encodeURIComponent(key)}/prices`),

  defineUnit: (input: DefineUnitInput): Promise<UnitDefinition> =>
    apiFetch('/analytics/units', json(input)),

  recordUnitPrice: (key: string, input: UnitPriceInput): Promise<UnitDefinition> =>
    apiFetch(`/analytics/units/${encodeURIComponent(key)}/prices`, json(input)),

  removeUnitPrice: (key: string, asOf: string): Promise<UnitDefinition> =>
    apiFetch(`/analytics/units/${encodeURIComponent(key)}/prices/${asOf}`, { method: 'DELETE' }),

  renameUnit: (key: string, label: string): Promise<UnitDefinition> =>
    apiFetch(`/analytics/units/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    }),

  removeUnit: (key: string): Promise<RemovedUnit> =>
    apiFetch(`/analytics/units/${encodeURIComponent(key)}`, { method: 'DELETE' }),
}

const MINOR_UNITS_PER_MAJOR = 100

/**
 * Minor units divide exactly by 100 as a double far beyond any plausible net
 * worth, and this value is only ever painted on screen — nothing reads it back.
 */
export function formatMinor(
  amountMinor: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return figureFormat(localeFor(currency), {
    style: 'currency',
    currency,
    ...ceiling(options),
    ...options,
  }).format(amountMinor / MINOR_UNITS_PER_MAJOR)
}

/**
 * Whole major units unless the caller says otherwise.
 *
 * A caller that asks for a minimum of two decimals and says nothing about the
 * maximum is asking for a range `Intl` refuses outright, and the refusal lands
 * as a blank screen rather than a wrong figure. The ceiling follows the floor.
 */
function ceiling(options: Intl.NumberFormatOptions): Intl.NumberFormatOptions {
  return { maximumFractionDigits: Math.max(0, options.minimumFractionDigits ?? 0) }
}

/**
 * The whole amount, the fraction and the unit, separately.
 *
 * Two things need the split. A price column holding "37,21 kr.", "130,95 €" and
 * "76.221,83 US$" right aligns on three different suffix widths, so tabular
 * figures line up with nothing until the unit has a column of its own. And a
 * design that steps the fraction down in size and colour cannot do it to a string.
 */
export function formatMinorParts(
  amountMinor: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
): { integer: string; fraction: string; amount: string; unit: string } {
  const parts = figureFormat(localeFor(currency), {
    style: 'currency',
    currency,
    ...ceiling(options),
    ...options,
  }).formatToParts(amountMinor / MINOR_UNITS_PER_MAJOR)

  const of = (...types: readonly Intl.NumberFormatPartTypes[]): string =>
    parts
      .filter((part) => types.includes(part.type))
      .map((part) => part.value)
      .join('')

  const integer = of('minusSign', 'plusSign', 'integer', 'group').trim()
  const fraction = of('decimal', 'fraction')

  return { integer, fraction, amount: `${integer}${fraction}`, unit: of('currency') }
}

/**
 * Past this `Intl` refuses the range rather than narrowing it, and the refusal
 * lands as a blank screen. Nothing is held to a twenty-first decimal.
 */
const MAX_FRACTION_DIGITS = 20

/**
 * A count of shares, shaped the way the household reads its money.
 *
 * A count is the one figure on the page with nothing to anchor its shape to: no
 * currency, no symbol, so "1.234" and "1,234" differ by a factor of a thousand
 * with nothing in the number to say which was meant. It follows the household
 * for the same reason a percentage does — it is read across the same row as an
 * amount, and a row that punctuates two ways is where the ambiguity bites.
 *
 * Every digit the quantity carries survives. `Intl` rounds to three places by
 * default, which would report a fractional fund position or a crypto position
 * as a different position than the one held; a caller wanting fewer decimals
 * rounds before it arrives. The string reaches `Intl` as a string for the same
 * reason it is stored as one: eighteen decimals do not fit in a double.
 */
export function formatQuantity(quantity: Quantity): string {
  const fraction = quantity.split('.')[1] ?? ''
  const places = Math.min(fraction.replace(/0+$/, '').length, MAX_FRACTION_DIGITS)
  return figureFormat(householdLocale(), {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(quantity as Intl.StringNumericLiteral)
}
